import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import dayjs from 'dayjs';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  useLoaderData,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import StickyBox from 'react-sticky-box';

import type { UppyFile } from '@uppy/core';

import { useStore } from '../store';

import {
  APIcreateMemory,
  APIupdateMemory,
} from '../api/Memory';

import MemoryVariantSelect from '../components/MemoryForm/VariantSelect';
import MemoryTypeSelect from '../components/MemoryForm/TypeSelect';
import MemoryLgcyTypeSelect from '../components/MemoryForm/LgcyTypeSelect';
import MemoryFiles from '../components/MemoryForm/Files';
import MemoryAbout from '../components/MemoryForm/About';
import MemorySprkMessage from '../components/MemoryForm/SprkMessage';
import MemorySprkRecipients from '../components/MemoryForm/SprkRecipients';
import MemoryTags from '../components/MemoryForm/Tags';
import MemoryEngagement from '../components/MemoryForm/Engagement';
import MemoryPreview from '../components/MemoryForm/Preview';
import MemoryButtons from '../components/Memory/Buttons';
import MemorySharing from '../components/MemorySharing';
import MemoryTimeline from '../components/Memory/Timeline';

import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/progress-bar/progress-bar.js';

import type { Asset } from '../types/asset';
import type { Memory } from '../types/memory';
import type { MemoryDataForm } from '../types/forms/memorydataform';
import type { Tag } from '../types/tag';

const MemoryEdit: React.FC = () => {
  const { profile } = useStore.getState();
  const { variant: variantParam } = useSearch({ strict: false });
  const nav = useNavigate();

  const {
    memory: data,
    sharedAssets,
    timeline,
  } = useLoaderData({ strict: false });
  const editMode = !!data;

  const [files, setFiles] = useState<UppyFile<Record<string, unknown>, Record<string, unknown>>[]>([]);
  const [memoryData, setMemoryData] = useState<MemoryDataForm>({
    variant: data?.variant || variantParam || (timeline ? 'memory' : 'sprk'),
    type: data?.type || (timeline ? timeline.lgcy_type : 'human'),
    lgcy_type: data?.lgcy_type || (timeline ? timeline.lgcy_type : ''),
    title: data?.title ||'',
    dedicated_to: data?.dedicated_to || (timeline ? timeline.dedicated_to : ''),
    name: data?.name || profile?.name || '',
    date: data?.date || '',
    content: data?.content || '',
    assets: data?.assets.map((f: Asset) => f.id) || [],
    tags: data?.tags ? data.tags.map((tag: Tag) => tag.id) : [],
    allow_comments: data?.allow_comments ?? true,
    allow_reactions: data?.allow_reactions ?? true,
    recipients: editMode ? [] : [{
      name: '',
      email: '',
      phone: '',
    }],
    timeline_id: data?.timeline_id || timeline?.id || null,
  });
  const [removedFiles, setRemovedFiles] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const { mutate: createMemory, isPending: isCreatePending, isError: isCreateError } = useMutation({
    mutationFn: async (requestData: MemoryDataForm): Promise<Memory> => {
      const response = await APIcreateMemory(requestData);
      return response.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (response: Memory) => {
      useStore.setState({ notice: { type: `${variant}.created` } });
      if (timeline) {
        nav({ href: `/timelines/${timeline.id}/${response.id}` });
      } else {
        nav({ href: `/memories/${response.id}` });
      }
    },
  });

  const { mutate: updateMemory, isPending: isUpdatePending, isError: isUpdateError } = useMutation({
    mutationFn: async (requestData: MemoryDataForm): Promise<Memory> => {
      const response = await APIupdateMemory(data.id, requestData);
      return response.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (response: Memory) => {
      useStore.setState({ notice: { type: `${variant}.updated` } });
      if (timeline) {
        nav({ href: `/timelines/${timeline.id}/${response.id}` });
      } else {
        nav({ href: `/memories/${response.id}` });
      }
    },
  });

  const isPending = editMode ? isUpdatePending : isCreatePending;
  const isError = editMode ? isUpdateError : isCreateError;

  const schemaMemory = z.object({
    variant: z.enum(['memory', 'sprk']),
    type: z.string(),
    lgcy_type: z.string().min(1, { message: "Please select one option" }),
    title: z.string().min(1, { message: "Title is required" }),
    dedicated_to: z.string().min(1, { message: "This field is required" }),
    name: z.string().min(1, { message: "This field is required" }),
    date: z.string().optional(),
    content: z.string().optional(),
    assets: z.array(z.string()).min(1, { message: "At least one file is required" }).max(25, { message: "At most 25 files are allowed" }),
    tags: z.array(z.number().or(z.undefined())).optional(),
    allow_comments: z.boolean(),
    allow_reactions: z.boolean(),
  });

  const schemaMemoryTimeline = z.object({
    variant: z.enum(['memory', 'sprk']),
    type: z.string(),
    lgcy_type: z.string().min(1, { message: "Please select one option" }),
    title: z.string().min(1, { message: "Title is required" }),
    dedicated_to: z.string().min(1, { message: "This field is required" }),
    name: z.string().min(1, { message: "This field is required" }),
    date: z.string().min(1, { message: "Date is required" }),
    content: z.string().optional(),
    assets: z.array(z.string()).min(1, { message: "At least one file is required" }).max(25, { message: "At most 25 files are allowed" }),
    tags: z.array(z.number().or(z.undefined())).optional(),
    allow_comments: z.boolean(),
    allow_reactions: z.boolean(),
  });

  const schemaSprk = z.object({
    variant: z.enum(['memory', 'sprk']),
    title: z.string().optional(),
    content: z.string().optional(),
    assets: z.array(z.string()).min(1, { message: "At least one file is required" }).max(1, { message: "At most 1 file is allowed" }),
    recipients: z.array(z.object({
      name: z.string().min(1, { message: "Name is required" }),
      email: z.string().min(1, { message: "Invalid email address" }),
      phone: z.string().optional(),
    }))
    .refine((recipients) => editMode || recipients.length > 0, { message: "At least one recipient is required" })
    .refine((recipients) => editMode || recipients.length <= 5, { message: "At most 5 recipients are allowed" }),
  });

  const {
    control,
    register,
    setValue,
    reset,
    formState: {
      errors,
      isValid,
      isSubmitted,
      submitCount,
      touchedFields,
    },
    handleSubmit,
    watch,
  } = useForm<MemoryDataForm>({
    defaultValues: {...memoryData},
    shouldUnregister: false,
    resolver: (data, context, options) => {
      const schema = data.variant === 'sprk' ? schemaSprk : (timeline ? schemaMemoryTimeline : schemaMemory);
      return (zodResolver(schema) as any)(data, context, options);
    },
  });

  const {
    fields: recipients,
    append: appendRecipient,
    remove: removeRecipient,
  } = useFieldArray({
    control,
    name: 'recipients',
  });

  const variant = watch('variant');

  const assets = useMemo(() => {
    return (editMode ? data.assets : [])
      .concat(sharedAssets.filter((a: Asset) => a.id !== data?.assets.map((f: Asset) => f.id).find((id: string) => id === a.id)))
      .concat(files)
      .map((file: (UppyFile<Record<string, unknown>, Record<string, unknown>> | Asset)) => {
      if (file.progress) {
        return {
          id: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).meta?.uuid || file.id,
          name: file.name,
          type: file.type,
          path: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).uploadURL ?? (file as Asset).path ?? '',
          size: file.size,
          thumbnail: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).preview as string ?? (file as Asset).thumbnail ?? '',
          progress: file.progress,
          error: file.error,
          status: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).response?.status ?? (file as Asset).status,
          local: true,
          uppyFileId: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).id,
        };
      } else {
        return {
          ...(file as unknown as Asset),
          local: false,
        };
      }
    })
    .filter((f: Asset) => memoryData.assets.includes(f.id) || f.local)
    .filter((f: Asset) => !removedFiles.includes(f.id))
    .filter((f: Asset) => variant === 'sprk' || !f.shared);
  }, [files, memoryData.assets, removedFiles, variant]);

  useEffect(() => {
    setValue('assets', assets.map((f: Asset) => f.id), { shouldDirty: true });
  }, [assets, setValue]);

  const onSubmit = async (data: MemoryDataForm) => {
    data.date = data.date ? dayjs(data.date).format('YYYY-MM-DD') : '';
    data.tags = data.tags ? data.tags = data.tags.filter(Number) : [];
    if (timeline) {
      data.timeline_id = timeline.id;
    }
    data.removed_files = removedFiles;

    if (editMode) {
      updateMemory(data);
    } else {
      createMemory(data);
    }
    reset();
  };

  useEffect(() => {
     if (isSubmitted && !isValid) {
      const firstErrorField = document.querySelector('.error-message');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [submitCount]);

  const addFile = (fileId: string) => {
    setMemoryData({
      ...memoryData,
      assets: [...memoryData.assets, fileId],
    });
    setValue('assets', [...memoryData.assets, fileId], { shouldDirty: true });
  };

  const selectFile = (fileId: string) => {
    setMemoryData({
      ...memoryData,
      assets: [fileId],
    });
    setValue('assets', [fileId], { shouldDirty: true });
  };

  const removeFile = (fileId: string) => {
    setMemoryData({
      ...memoryData,
      assets: memoryData.assets.filter((f) => f !== fileId),
    });
    setValue('assets', memoryData.assets.filter((f) => f !== fileId), { shouldDirty: true });
    setRemovedFiles([...removedFiles, fileId]);
  };

  useEffect(() => {
    setMemoryData({
      ...memoryData,
      variant,
    });
  }, [variant]);

  const getPublishButtonText = () => {
    let text = '';

    if (variant === 'sprk') {
      if (editMode) {
        text = 'Update SPRK';
      } else {
        text = 'Send SPRK';
      }
    }

    if (variant === 'memory') {
      if (editMode) {
        text = 'Update Memory';
      } else {
        if (timeline) {
          text = 'Publish Memory to Timeline';
        } else {
          text = 'Publish Memory';
        }
      }
    }

    return text;
  }

  return (
    <div className="container memory-create-container">
      {!editMode && (
        <div className="page-header">
          <div className="page-eyebrow">
            Memory maker
          </div>
          <h2 className="page-title">
            What <em>moment</em> would you like to preserve?
          </h2>
          {timeline && (
            <h4>You are adding a new Memory to the Timeline: {timeline.dedicated_to}</h4>
          )}
        </div>
      )}
      <div className="wa-flank wa-flank:end wa-gap-0" style={{ '--flank-size': '450px' } as React.CSSProperties}>
        <div className="memory-form">
          <>
            {isError && (
              <wa-callout variant="danger">
                <i className="fa-regular fa-circle-exclamation" slot="icon" />
                <strong>An error occurred while creating the Memory.</strong>
              </wa-callout>
            )}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {!timeline && (
                <>
                  <MemoryVariantSelect
                    variant={memoryData.variant}
                    errors={errors}
                    register={register}
                  />
                  {variant === 'memory' && (
                    <>
                      <MemoryTypeSelect
                        selectedType={watch('type')}
                        register={register}
                      />
                      <MemoryLgcyTypeSelect
                        selectedType={watch('lgcy_type')}
                        errors={errors}
                        register={register}
                      />
                    </>
                  )}
                </>
              )}
              <MemoryFiles
                addFile={addFile}
                selectFile={selectFile}
                removeFile={removeFile}
                setUploading={setUploading}
                setFiles={setFiles}
                files={assets}
                errors={errors}
                variant={variant}
                sharedAssets={sharedAssets}
                selectedAssets={memoryData.assets}
                inTimeline={!!timeline}
              />
              {variant === 'memory' && (
                <MemoryAbout
                  memoryData={memoryData}
                  control={control}
                  register={register}
                  errors={errors}
                />
              )}
              {variant === 'sprk' && (
                <MemorySprkMessage
                  memoryData={memoryData}
                  register={register}
                />
              )}
              {variant === 'memory' && (
                <>
                  <MemoryTags
                    tags={memoryData.tags}
                    inTimeline={!!timeline}
                    control={control}
                  />
                  {!timeline && (
                    <MemoryEngagement
                      allowComments={memoryData.allow_comments}
                      allowReactions={memoryData.allow_reactions}
                      register={register}
                      setValue={setValue}
                    />
                  )}
                </>
              )}
              {variant === 'sprk' && (
                <MemorySprkRecipients
                  recipients={recipients}
                  errors={errors}
                  addRecipient={appendRecipient}
                  setValue={setValue}
                  removeRecipient={removeRecipient}
                />
              )}
              <div className="wa-cluster memory-form-actions inner">
                <wa-button
                  type="button"
                  className="hidden"
                  appearance="outlined"
                  disabled={isPending || uploading}
                >
                  {editMode ? 'Change to draft' : 'Save draft'}
                </wa-button>
                <wa-button
                  type="submit"
                  disabled={isPending || uploading}
                >
                  {isPending ? (
                    <>
                      <wa-spinner slot="start" />
                      Saving...
                    </>
                  ) : (
                    <>
                      {getPublishButtonText()}
                    </>
                  )}
                </wa-button>
              </div>
            </form>
          </>
        </div>
        <div className="memory-preview inner">
          <StickyBox offsetTop={20}>
            {editMode && (
              <MemoryButtons memory={data} page="edit" />
            )}
            {timeline && (
              <MemoryTimeline memory={data} timeline={timeline} />
            )}
            {!timeline && (
              <>
                <div className="page-eyebrow">
                  Live Preview
                </div>
                <MemoryPreview
                  memoryData={memoryData}
                  files={assets}
                  watch={watch}
                  isEditMode={editMode}
                  touchedFields={touchedFields as Record<string, boolean>}
                />
              </>
            )}
            {editMode && (
              <MemorySharing memory={data} />
            )}
          </StickyBox>
        </div>
      </div>
    </div>
  );
}

export default MemoryEdit;
